import React from 'react';
import {useStyles} from '../../hooks/styles';
import {Platform, TouchableOpacity, View} from 'react-native';
import {Text} from '../text';
import {useScreenState} from '../../hooks/useScreenState';
import {images} from '../../../assets/icons';
import {useTheme} from '../../theme';

export interface EvenOddListItemContentFactoryProps {
  isMasked: boolean;
  onDisableMasking: (value: boolean) => void;
}

export type EvenOddListItemContentFactory = (props: EvenOddListItemContentFactoryProps) => JSX.Element;

interface EvenOddListItemProps extends EvenOddListItemContentFactoryProps {
  index?: number;
  content: JSX.Element | EvenOddListItemContentFactory;
  isEven: boolean;
  // position: 'first' | 'last' | 'medium' | 'single';
  ignoreSeparation?: boolean;
  isRounded: boolean;
}

export interface Props {
  testIDPrefix: string;
  title?: string;
  ignoreSeparation?: boolean;
  list: ({value: JSX.Element | EvenOddListItemContentFactory} | null)[];
  hasPrivateData?: boolean;
}

const EvenOddListItem = (props: EvenOddListItemProps) => {
  const {isEven, content, isMasked, onDisableMasking, isRounded, index} = props;
  const styles = useStyles(({theme}) => {
    return {
      container: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        paddingVertical: 10,
        minHeight: 42,
        borderRadius: isRounded ? 14 : 0,
      },
      evenContainer: {
        backgroundColor: theme.evenOddList.oddContainer,
      },
      oddContainer: {
        backgroundColor: theme.evenOddList.evenContainer,
      },
      label: {
        ...theme.fonts.label,
        color: theme.color.labelText,
        opacity: 1,
      },
      prop: {
        textAlign: 'right',
        maxWidth: 180,
        ...theme.fonts.body,
        color: theme.color.textColor,
        opacity: 1,
      },
    };
  });
  const style = [styles.container, isEven ? styles.evenContainer : styles.oddContainer];
  return (
    <View testID={`event-oddlist-item-${index}`} style={style}>
      {typeof content === 'function' ? content({isMasked, onDisableMasking}) : content}
    </View>
  );
};

export const EvenOddList = (props: Props) => {
  const {hasPrivateData} = props;
  const [isMasked, setMasked] = useScreenState(true);
  const [isMaskingDisabled, setMaskingDisabled] = React.useState(false);
  const lastMaskedState = React.useRef(true);
  const isSingleListItem = props.list?.length === 1;

  const handleDisableMasking = React.useCallback(
    (isDisabled: boolean) => {
      if (isDisabled) {
        lastMaskedState.current = isMasked;
        setMasked(false);
      } else {
        setMasked(lastMaskedState.current);
      }
      setMaskingDisabled(isDisabled);
    },
    [isMasked],
  );

  const theme = useTheme();
  const styles = useStyles(() => {
    return {
      title: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 9,
      },
      titleText: {
        ...theme.fonts.h6,
        textAlign: 'center',
        flexGrow: 1,
      },
      titleVisibilityButton: {
        width: 34,
        height: 34,
        justifyContent: 'center',
        alignItems: 'center',
      },
      container: {
        borderRadius: 14,
        overflow: isSingleListItem ? 'visible' : 'hidden',
      },
    };
  });
  const visibilityIconSize = {
    width: 25,
    height: 25,
    strokeWidth: Platform.OS === 'web' ? 1 : 0,
  };
  const visibilityIconColor = isMaskingDisabled ? theme.color.iconGrayColor : theme.color.textColor;

  if (!props.list?.length) {
    return null;
  }
  return (
    <View>
      {props.title ? (
        <View style={styles.title}>
          <Text testID={`${props.testIDPrefix}-title-id`} style={styles.titleText}>
            {props.title}
          </Text>
          {hasPrivateData && (
            <TouchableOpacity
              testID='event-odd-list-private-data-id'
              disabled={isMaskingDisabled}
              style={styles.titleVisibilityButton}
              onPress={() => setMasked(!isMasked)}>
              {isMasked ? (
                <images.EyeOffFat color={visibilityIconColor} {...visibilityIconSize} />
              ) : (
                <images.EyeFat color={visibilityIconColor} {...visibilityIconSize} />
              )}
            </TouchableOpacity>
          )}
        </View>
      ) : null}
      <View style={styles.container}>
        {props.list.map((prop, index) => {
          if (!prop) {
            return null;
          }
          return (
            <EvenOddListItem
              key={index}
              index={index}
              isRounded={isSingleListItem}
              content={prop.value}
              isEven={props.ignoreSeparation ? true : index % 2 === 0}
              isMasked={isMasked}
              onDisableMasking={handleDisableMasking}
            />
          );
        })}
      </View>
    </View>
  );
};
